<script setup lang="ts">
// A dense ledger table over one tab's rows, shared by every workbench. It owns
// exactly one thing — the order — plus rendering the multi-select boxes when the
// page asks for them. Columns, rows, selection and order all arrive as props.
import { computed, ref, watch } from "vue";

import type { LedgerColumn, LedgerRow, LedgerSelection } from "./ledger";
import { ledgerRowKey } from "./ledger";

interface Props {
	columns: LedgerColumn[];
	rows: LedgerRow[];
	selection: LedgerSelection | null;
	defaultSort: { key: string; descending: boolean };
	/** Enables the leading checkbox column and the additive row click. */
	multi?: boolean;
	selectedKeys?: Set<string>;
}

const props = withDefaults(defineProps<Props>(), {
	multi: false,
	selectedKeys: () => new Set<string>(),
});

const emit = defineEmits<{
	select: [row: LedgerRow, additive: boolean];
	toggle: [row: LedgerRow];
}>();

const sortKey = ref(props.defaultSort.key);
const sortDescending = ref(props.defaultSort.descending);

watch(
	() => props.defaultSort,
	(sort) => {
		if (sort.key === sortKey.value && sort.descending === sortDescending.value)
			return;
		sortKey.value = sort.key;
		sortDescending.value = sort.descending;
	},
);

function sortBy(key: string) {
	if (key === sortKey.value) sortDescending.value = !sortDescending.value;
	else {
		sortKey.value = key;
		sortDescending.value = false;
	}
}

const sortIndex = computed(() =>
	props.columns.findIndex((column) => column.key === sortKey.value),
);

const sortedRows = computed<LedgerRow[]>(() => {
	const index = sortIndex.value;
	if (index < 0) return props.rows;
	return props.rows
		.map((row, order) => ({ row, order }))
		.sort((left, right) => {
			const a = left.row.sort[index] ?? null;
			const b = right.row.sort[index] ?? null;
			if (a === null || b === null) {
				if (a === b) return left.order - right.order;
				return a === null ? 1 : -1;
			}
			const result =
				typeof a === "number" && typeof b === "number"
					? a - b
					: String(a).localeCompare(String(b));
			if (result !== 0) return sortDescending.value ? -result : result;
			return left.order - right.order;
		})
		.map((entry) => entry.row);
});

function isSelected(row: LedgerRow): boolean {
	return props.selection?.kind === row.kind && props.selection?.id === row.id;
}

function isChecked(row: LedgerRow): boolean {
	return props.selectedKeys.has(ledgerRowKey(row));
}

function onRowClick(row: LedgerRow, event: MouseEvent) {
	emit(
		"select",
		row,
		props.multi && (event.ctrlKey || event.metaKey || event.shiftKey),
	);
}

/** The checkbox column shifts every data column by one. */
function columnFor(index: number): LedgerColumn | undefined {
	return props.columns[props.multi ? index + 1 : index];
}
</script>

<template>
	<div class="ledger-scroll">
		<table class="ledger-table">
			<thead>
				<tr>
					<th v-if="multi" class="ledger-th ledger-pick" scope="col"></th>
					<th
						v-for="column in columns"
						:key="column.key"
						scope="col"
						class="ledger-th"
						:class="{ 'ledger-num': column.align === 'end' }"
						:style="column.width ? { width: column.width } : undefined"
						:aria-sort="
							column.key === sortKey
								? sortDescending
									? 'descending'
									: 'ascending'
								: 'none'
						"
						@click="sortBy(column.key)"
					>
						{{ column.label }}
						<i
							v-if="column.key === sortKey"
							class="bi ledger-sort-mark"
							:class="
								sortDescending ? 'bi-caret-down-fill' : 'bi-caret-up-fill'
							"
						></i>
					</th>
				</tr>
			</thead>
			<tbody v-if="rows.length > 0">
				<tr
					v-for="row in sortedRows"
					:key="ledgerRowKey(row)"
					class="ledger-row"
					:class="{ selected: isSelected(row) || isChecked(row) }"
					tabindex="0"
					@click="onRowClick(row, $event)"
					@keydown.enter.prevent="emit('select', row, false)"
				>
					<td v-if="multi" class="ledger-cell ledger-pick">
						<input
							type="checkbox"
							class="form-check-input"
							:checked="isChecked(row)"
							:aria-label="`Select ${row.kind} ${row.id}`"
							@click.stop
							@change="emit('toggle', row)"
						/>
					</td>
					<td
						v-for="(cell, index) in row.cells"
						:key="index"
						class="ledger-cell"
						:class="{ 'ledger-num': columnFor(index)?.align === 'end' }"
					>
						<span
							v-if="index === 0 && row.swatch"
							class="ledger-swatch"
							:style="{ background: row.swatch }"
						></span
						>{{ cell }}
					</td>
				</tr>
			</tbody>
		</table>
		<p v-if="rows.length === 0" class="ledger-empty">Nothing to list.</p>
	</div>
</template>

<style scoped>
.ledger-scroll {
	flex: 1;
	min-height: 0;
	overflow: auto;
	position: relative;
}
.ledger-table {
	width: 100%;
	border-collapse: separate;
	border-spacing: 0;
	table-layout: fixed;
	font-size: 0.75rem;
}
.ledger-th,
.ledger-cell {
	padding: 0 6px;
	box-sizing: border-box;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: clip;
}
.ledger-th {
	position: sticky;
	top: 0;
	z-index: 2;
	height: 28px;
	line-height: 28px;
	background: #f1f3f5;
	border-bottom: 1px solid #dee2e6;
	font-weight: 600;
	color: #343a40;
	text-align: left;
	user-select: none;
	cursor: pointer;
}
.ledger-th:hover {
	background: #e9ecef;
}
.ledger-pick {
	width: 2rem;
	text-align: center;
}
.ledger-pick .form-check-input {
	margin: 0;
}
.ledger-sort-mark {
	margin-left: 3px;
	color: #0d6efd;
}
.ledger-row {
	height: 28px;
	cursor: pointer;
}
.ledger-row:focus-visible {
	outline: 2px solid #0d6efd;
	outline-offset: -2px;
}
.ledger-cell {
	height: 28px;
	line-height: 27px;
	box-shadow: inset 0 -1px 0 #eef0f2;
	color: #212529;
	background: #fff;
}
.ledger-row:hover .ledger-cell {
	background: #f8f9fa;
}
.ledger-row.selected .ledger-cell {
	background: #e7f1ff;
}
.ledger-num {
	text-align: right;
	font-variant-numeric: tabular-nums;
}
.ledger-swatch {
	display: inline-block;
	flex: 0 0 auto;
	width: 9px;
	height: 9px;
	margin-right: 5px;
	border-radius: 50%;
	box-shadow: inset 0 0 0 1px #ced4da;
	vertical-align: -1px;
}
.ledger-empty {
	margin: 0;
	padding: 6px;
	color: #6c757d;
	font-size: 0.75rem;
}
</style>
